import { useEffect, useContext, useState, useRef } from 'react';
import { cloneDeep } from 'lodash';
import clsx from 'clsx';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import '../css/AgGridStyles.css';
import { TablePagination } from '@material-ui/core';
import { PageContext } from '../PageContext';
import { UserContext } from '../../../contexts';
import { getAgGridOptions } from '../../../common/grid/AgGridOptions';
import { ApplicationRoles } from '../../../../helpers';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    root: {
    },
    overlayContainer: {
        position: "relative",
        height: "100%",
    },
    overlay: {
        position: "absolute",
        top: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        opacity: ".7"
    }
}));

export const ContentGrid = ({
    dataSource,
    disabled,
    onLoadMore,
    onUpdateRow,
    onUpdateRows,
    onUpdateFillRows,
    onSelectedRows,
    ...props }) => {
    const classes = useStyles();

    const {
        isUserInRole,
        isUserInRoles
    } = useContext(UserContext);

    const {
        state,
        isBusy,
        setGridApi,
        setGridColumnApi,
        saveColumnVisibleStatus,
        saveColumnPinnedStatus,
        saveFilterModelStatus,
        setSelectedRowsCount
    } = useContext(PageContext);

    const {
        table,
        dataTransaction
    } = state;

    const [gridOptions, setGridOptions] = useState(
        getAgGridOptions(
            table,
            {
                showColumnTypes: isUserInRoles([ApplicationRoles.Administrator, ApplicationRoles.Editor]),
                editable: isUserInRoles([ApplicationRoles.Administrator, ApplicationRoles.Editor])
            }
        )
    );

    const agGridReadyHandler = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    }

    const rowValueChangedHandler = async (e) => {
        const rowNode = e.node;
        const rowData = e.data;
        const oldHash = rowData.changeHash;
        const newHash = rowData.getChangeHash();
        if (oldHash !== newHash && onUpdateRow) {
            if (!isCopyPaste.current) {
                onUpdateRow(rowNode.rowIndex);
            } else {
                cpRowIndexes.current.push(rowNode.rowIndex);
            }
        }
    }

    const fillChangeHandler = async (e) => {
        let startIndex =  e.initialRange.startRow.rowIndex;
        let endIndex;
        let allIndexes = [];
        if(startIndex === e.finalRange.endRow.rowIndex)
        {
            // Bottom to top
            endIndex = e.finalRange.startRow.rowIndex;
            for(let i = endIndex; i < startIndex; i++)
            {
                allIndexes.push(i);
            }
        } else {
            // Top to bottom
            endIndex = e.finalRange.endRow.rowIndex;
            for(let i = endIndex; i > startIndex; i--)
            {
                allIndexes.push(i);
            }
        }
        onUpdateRows(allIndexes);
    }

    const isCopyPaste = useRef(false);
    const cpRowIndexes = useRef(null);
    const pasteStartHandler = (e) => {
        isCopyPaste.current = true;
        cpRowIndexes.current = [];
    }

    const pasteEndHandler = (e) => {
        isCopyPaste.current = false;
        if (cpRowIndexes.current.length > 0 && onUpdateRows) {
            onUpdateRows(cloneDeep(cpRowIndexes.current));
            cpRowIndexes.current = null;
        }
    }

    const [pageIndex, setPageIndex] = useState(-1);
    const pageIndexChangeHandler = (e, index) => {
        setPageIndex(index);
    }

    const itemsPerPage = dataSource.pageSize;
    const [pageSize, setPageSize] = useState(-1);
    const pageSizeChangeHandler = (e) => {
        setPageSize(e.target.value)
    }

    const columnVisibleHandler = (e) => {
        if (!isBusy("resetGridState")) {
            const columnName = e.columns[0].colId;
            const isVisible = e.columns[0].visible;
            saveColumnVisibleStatus(columnName, isVisible);
        }
    }

    const columnPinnedHandler = (e) => {
        if (!isBusy("resetGridState")) {
            const columnName = e.columns[0].colId;
            const pinned = e.pinned;
            saveColumnPinnedStatus(columnName, pinned);
        }
    }

    const filterChangedHandler = (e) => {
        if (!isBusy("resetGridState")) {
            const filterModel = e.api.getFilterModel();
            saveFilterModelStatus(filterModel);
        }
    }

    useEffect(() => {
        if ((pageIndex !== -1 || pageSize !== -1) && onLoadMore) {
            onLoadMore(Math.max(0, pageIndex), Math.max(itemsPerPage, pageSize));
        }
    }, [pageIndex, pageSize]);

    const [status, setStatus] = useState({ canChangePage: true });
    useEffect(() => {
        if (dataTransaction) {
            setStatus({
                canChangePage: !dataTransaction.hasPendingChanges
            });
        }
    }, [dataTransaction]);

    return (
        <div className={clsx("ag-theme-alpine", classes.root, props.className)}>
            <div className={classes.overlayContainer}>
                {/* {status.canChangePage &&
                    <TablePagination
                        component="div"
                        count={dataSource ? dataSource.currentRowCount : 0}
                        onPageChange={pageIndexChangeHandler}
                        onRowsPerPageChange={pageSizeChangeHandler}
                        page={Math.max(0, pageIndex)}
                        rowsPerPage={Math.max(itemsPerPage, pageSize)}
                        rowsPerPageOptions={[itemsPerPage / 2, itemsPerPage, itemsPerPage * 2]}
                    />
                } */}
                <AgGridReact
                    className="ag-theme-alpine"
                    theme={'legacy'}
                    gridOptions={gridOptions}
                    serverSideDatasource={dataSource}
                    onGridReady={agGridReadyHandler}
                    onRowValueChanged={rowValueChangedHandler}
                    enableFillHandle={true}
                    onRowSelected={(row) => { setSelectedRowsCount(row.api.getSelectedNodes().length);  }}
                    onPasteStart={pasteStartHandler}
                    onPasteEnd={pasteEndHandler}
                    onColumnVisible={columnVisibleHandler}
                    onColumnPinned={columnPinnedHandler}
                    // onCellValueChanged={rowValueChangedHandler}
                    onFilterChanged={filterChangedHandler}
                    onFillEnd={fillChangeHandler }
                />
                {status.canChangePage &&
                    <TablePagination
                        component="div"
                        count={dataSource ? dataSource.currentRowCount : 0}
                        onPageChange={pageIndexChangeHandler}
                        onRowsPerPageChange={pageSizeChangeHandler}
                        page={Math.max(0, pageIndex)}
                        rowsPerPage={Math.max(itemsPerPage, pageSize)}
                        rowsPerPageOptions={[itemsPerPage / 2, itemsPerPage, itemsPerPage * 2]}
                    />
                }
                {disabled && <div className={classes.overlay} />}
            </div>
        </div>
    );
}